/**
 * The header search — Wallet's magnifier, wired to AIM's existing global
 * search endpoint.
 *
 * `GET /py-api/v1/search` (`backend/api/app/routers/search.py`) already spans
 * income, expense, transfer, account, category and label, and it is GATED
 * SERVER-SIDE on the `records` page permission with the scope escalation guard
 * applied inside the service. So this sheet needs no gate of its own and
 * cannot become a way to read rows the user could not already read: a USER
 * forging `?all=true` still resolves to their own data, and a user without the
 * `records` grant gets a 403 and an empty sheet.
 *
 * Each hit carries a server-computed `href`. It is still CLASSIFIED before we
 * navigate (`classifyHref`) — a link the registry does not know about is not
 * followed, it falls back to the Records page with the query applied. That is
 * paranoia about a field from the network becoming a navigation target, not a
 * suspicion of this particular backend.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Search, X } from "lucide-react";
import { apiFetch } from "@/lib/api/client";
import { apiUrl } from "@/lib/api/config";
import { cn, formatINR } from "@/lib/utils";
import { Sheet } from "./Sheet";
import { classifyHref, RECORDS_HREF } from "./nav";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;
const PREVIEW_LIMIT = 12;

interface SearchHit {
  type: string;
  id: string;
  title: string;
  subtitle?: string | null;
  amountPaise?: number | null;
  date?: string | null;
  archived?: boolean;
  href: string;
}

interface SearchResponse {
  items: SearchHit[];
}

const TYPE_LABEL: Record<string, string> = {
  income: "Income",
  expense: "Expense",
  transfer: "Transfer",
  account: "Account",
  category: "Category",
  label: "Label",
};

/** Only same-origin, registry-or-shell paths are ever navigated to. */
function safeHref(href: string): string | null {
  if (!href.startsWith("/") || href.startsWith("//")) return null;
  return classifyHref(href) === "unrouted" ? null : href;
}

export function SearchSheet({ open, onClose }: { open: boolean; onClose(): void }) {
  const [text, setText] = useState("");
  const [debounced, setDebounced] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    // A search sheet that opens without the keyboard is a search sheet you tap
    // twice. The rAF lets the panel's enter animation start first.
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(text.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  const enabled = open && debounced.length >= MIN_QUERY_LENGTH;

  const { data, isFetching, isError } = useQuery({
    queryKey: ["wallet-shell-search", debounced],
    enabled,
    staleTime: 15_000,
    queryFn: async (): Promise<SearchResponse> => {
      const res = await apiFetch(apiUrl("search", "", { q: debounced, limit: PREVIEW_LIMIT }));
      if (!res.ok) throw new Error(`Search failed (${res.status})`);
      return (await res.json()) as SearchResponse;
    },
  });

  const hits = useMemo(() => data?.items ?? [], [data]);

  const seeAll = () => {
    onClose();
    navigate(`${RECORDS_HREF}?q=${encodeURIComponent(debounced)}`);
  };

  const openHit = (hit: SearchHit) => {
    const target = safeHref(hit.href);
    onClose();
    navigate(target ?? `${RECORDS_HREF}?q=${encodeURIComponent(debounced)}`);
  };

  return (
    <Sheet open={open} onClose={onClose} title="Search" bareHeader variant="full">
      <div className="flex h-full flex-col">
        <div className="flex shrink-0 items-center gap-2 border-b border-[var(--cp-line-2)] px-3 py-2.5">
          <Search className="h-4 w-4 shrink-0 text-[var(--cp-ink-3)]" />
          <input
            ref={inputRef}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && debounced.length >= MIN_QUERY_LENGTH) seeAll();
            }}
            type="search"
            enterKeyHint="search"
            placeholder="Search records, accounts, categories…"
            aria-label="Search"
            className="min-w-0 flex-1 bg-transparent text-[15px] text-[var(--cp-ink)] outline-none placeholder:text-[var(--cp-ink-3)]"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close search"
            className="cp-icon-btn h-8 w-8"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {debounced.length < MIN_QUERY_LENGTH && (
            <p className="px-4 py-6 text-sm text-[var(--cp-ink-3)]">
              Type at least {MIN_QUERY_LENGTH} characters.
            </p>
          )}

          {enabled && isError && (
            <p className="px-4 py-6 text-sm text-[var(--cp-rose-ink)]">
              Search is unavailable right now.
            </p>
          )}

          {enabled && !isError && hits.length === 0 && !isFetching && (
            <p className="px-4 py-6 text-sm text-[var(--cp-ink-3)]">
              Nothing matched “{debounced}”.
            </p>
          )}

          {hits.map((hit) => (
            <button
              key={`${hit.type}:${hit.id}`}
              type="button"
              onClick={() => openHit(hit)}
              className="flex w-full items-center gap-3 border-b border-[var(--cp-line-2)] px-4 py-3 text-left transition-colors hover:bg-[var(--cp-surface-2)]"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-[var(--cp-ink)]">
                  {hit.title}
                </span>
                <span className="mt-0.5 block truncate text-xs text-[var(--cp-ink-3)]">
                  {TYPE_LABEL[hit.type] ?? hit.type}
                  {hit.subtitle ? ` · ${hit.subtitle}` : ""}
                  {hit.date ? ` · ${hit.date}` : ""}
                </span>
              </span>
              {typeof hit.amountPaise === "number" && (
                <span
                  className={cn(
                    "cp-num shrink-0 text-sm font-semibold",
                    hit.type === "income" ? "text-[var(--cp-emerald)]" : "text-[var(--cp-ink)]",
                  )}
                >
                  {formatINR(hit.amountPaise)}
                </span>
              )}
            </button>
          ))}

          {enabled && hits.length > 0 && (
            <button
              type="button"
              onClick={seeAll}
              className="flex w-full items-center justify-center gap-2 px-4 py-3.5 text-sm font-medium text-[var(--cp-accent-ink)] transition-colors hover:bg-[var(--cp-accent-wash)]"
            >
              See all in Records
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </Sheet>
  );
}
